import {
  find,
  isUndefined,
  range,
  result,
  sortBy,
  trim,
  map,
  groupBy
} from 'libraries/lodash'
import { LEGISLATION_PARTIAL_NOTES } from 'constants/feature-flags'
import { TAKEOVER_INTERNAL } from 'constants/urls'
import { parseJson, touch, deepPush } from 'helpers/transforms'

const toContext = data => data.map(({ title, id, level, ref }) => ({ title, id, level, number: ref || null }))

const paragraphsToXml = function (paragraphs) {
  let xmlStr = ''

  const rangeTag = tag => indentLevel =>
    range(0, indentLevel + 1).reduce(a => a + tag, '')
  const openTag = rangeTag('<p>')
  const closeTag = rangeTag('</p>')

  const toReference = textPrefixes => {
    return textPrefixes.length
      ? '<reference>' +
          textPrefixes.reduce(
            (a, { text }, index) =>
              `${a}<stacked-reference-${textPrefixes.length -
                index}>${text}</stacked-reference-${textPrefixes.length -
                index}>`,
            ''
          ) +
          '</reference>'
      : ''
  }

  paragraphs.forEach(({ text, emphasis, indentLevel, textPrefixes }) => {
    xmlStr += openTag(indentLevel + textPrefixes.length)
    xmlStr += toReference(textPrefixes)
    xmlStr += text
    xmlStr += closeTag(indentLevel + textPrefixes.length)
  })

  return xmlStr
}

export const toLegislationAct = function (response, p) {
  if (typeof response === 'string') {
    response = parseJson(response)
  }
  const data = result(response, '[0].content') || response
  const nodes = {}

  const getNode = id => nodes[id]

  const toNode = function (node, parentPath = []) {
    const {
      schedules = [],
      parts = [],
      chapters = [],
      crossheadings = [],
      sections = []
    } = node
    let { id, level, hoverIcon, title, number, ref, levelName, folder, category, isArchived = false, content = null } = node
    const { partialnotes = null, partialnote = null, jurisdictionnote = null } = node
    if (content) {
      if (partialnote && !LEGISLATION_PARTIAL_NOTES) {
        content = `<partialnote>${partialnote}</partialnote>${content}`
      }
      if (partialnotes && LEGISLATION_PARTIAL_NOTES) {
        const partialnotesHtml = partialnotes.map(pn => `<p><partialnote>${pn}</partialnote></p>`).join('')
        content = `${partialnotesHtml}${content}`
      }
      if (jurisdictionnote) {
        content = `<jurisdictionnote>${jurisdictionnote}</jurisdictionnote>${content}`
      }
    }

    if (!id) {
      return null
    }
    const path = [...parentPath, id]
    const descendants = [
      ...parts,
      ...chapters,
      ...crossheadings,
      ...sections,
      ...schedules
    ]
      .map(n => toNode(n, path))
      .filter(x => !!x)
    const hasParts = !parentPath.length && !!parts.length
    const hasSchedules = !parentPath.length && !!schedules.length
    const hasCrossheadings = !parentPath.length && !!crossheadings.length
    const isLeaf = !!node.content
    let leaves

    if (isLeaf) {
      leaves = [id]
    } else {
      leaves = descendants.reduce((a, n) => [...a, ...n.leaves], [])
    }

    nodes[id] = {
      hoverIcon,
      id,
      level,
      levelName: levelName || level,
      title,
      number: number || ref,
      content,
      descendants: descendants.map(a => a.id),
      leaves,
      hasParts,
      hasSchedules,
      hasCrossheadings,
      isLeaf,
      folder,
      category,
      isArchived,
      path,
      getNode
    }
    return { leaves, id }
  }

  const root = toNode(data).id

  return {
    root,
    nodes
  }
}

export const toLegislationReference = function (data) {
  data = parseJson(data)
  const mergeHighlighted = (a, b) =>
    (!a && b) ||
    Object.assign(a, { highlighted: [...a.highlighted, ...b.highlighted] })
  const toAct = e =>
    Object.assign(toLegislationAct(e.content), {
      context: toContext(e.context),
      highlighted: e.highlighted
    })

  const mappedToAct = map(data, toAct)
  const grouped = groupBy(mappedToAct, 'root')

  return map(grouped, acc => acc.reduce(mergeHighlighted, null))
}

export const toLegislationActs = function (data) {
  return toLegislationActsNoJson(parseJson(data))
}

const toLegislationActsNoJson = function (data) {
  return sortBy(
    [
      ...data.map(act => ({
        number: act.ref,
        level: act.level,
        levelName: act.levelName,
        folder: act.folder,
        category: act.category,
        actNote: act.actNote,
        title: act.title,
        id: act.id,
        hidden: false,
        archive: act.isArchived || false,
        isComplete: act.isComplete
      })),
      {
        id: `${TAKEOVER_INTERNAL}`,
        level: 'act',
        title: 'The Takeover Code',
        number: '',
        hidden: true,
        archive: false
      }
    ],
    'title'
  )
}

export const toLegislationRelatedQas = function (data) {
  const { results } = parseJson(data)
  return results.map(q => ({
    question: q.questionExcerpt,
    answer: q.answerExcerpt,
    tocId: q.bookmarkId || q.tocID,
    sectionId: q.parent_id.toString(),
    questionNumber: +q.question_num
  }))
}

export const toLegislationSearch = function (data) {
  data = parseJson(data)
  return {
    resultCount: +data.totalNumberResults,
    results: data.results.map(r => ({
      id: r.id,
      title: r.title || null,
      number: r.ref || null,
      content: r.content,
      level: r.level || 'section',
      isArchived: r.archived,
      context: toContext(r.context)
    }))
  }
}

export const toLegislationFolders = function (data) {
  return sortBy(
    [
      ...parseJson(data).map(folder => ({
        name: folder.name,
        categories: toLegislationCategories(folder.categories)
      }))
    ],
    'name'
  )
}

const toLegislationCategories = function (data) {
  return sortBy(
    [
      ...data.map(category => ({
        name: category.name,
        legislation: toLegislationActsNoJson(category.legislation)
      }))
    ],
    'name'
  )
}

export const toTakeoverCode = function (response) {
  response = parseJson(response)

  const versionId = response.id
  const nodes = {}
  const sections = {}
  sortBy(response.sections, 'id').forEach(node => {
    touch(sections, node.id, { children: [] })
    node.parentId && deepPush(sections, [node.parentId, 'children'], node.id)
    sections[node.id] = {
      ...sections[node.id],
      ...node
    }
  })
  const { id: rootId } = find(response.sections, ({ parentId }) => !parentId)
  const toId = (path = []) => `${TAKEOVER_INTERNAL}:${path.join(':')}`
  const getNode = id => nodes[id]
  const extractTitleRegexp = /^([\d\.]+|\([a-z]+\)|SECTION\s[A-Z]\.?|RULE\s\d+\.?|APPENDIX\s\d+)\s(.*)$/ // eslint-disable-line

  const getSiblings = function (id) {
    const { parentId } = sections[id] || {}
    const { children = [] } = sections[parentId] || {}
    return children.map(id => sections[id])
  }

  const siblingsHaveChildren = function (id) {
    if (isUndefined(sections[id].parentId)) {
      return true
    }
    return getSiblings(id).reduce((a, s) => a || s.children.length > 0, false)
  }

  const toNode = function (node, parentPath = []) {
    let {
      id,
      paragraphs,
      title,
      children
    } = node
    const path = [...parentPath, id]
    const [, number = '', trimmedTitle = title] =
      `${title} `.match(extractTitleRegexp) || []
    const [levelName = ' '] = number.match(/[A-Z]{2,}/) || []
    const descendants = children.map(sectionId =>
      toNode(sections[sectionId], path)
    )
    const isLeaf = !siblingsHaveChildren(id) && levelName !== ' '
    id = toId(path)
    const leaves = isLeaf
      ? [id]
      : descendants.reduce((a, n) => [...a, ...n.leaves], [])

    if (
      paragraphs.length &&
      (!isLeaf || levelName.toLowerCase() === 'section')
    ) {
      const pre = toContentNode({ paragraphs }, [...path, 'PRE'], node)
      descendants.unshift(pre)
      leaves.unshift(pre.id)
    }

    nodes[id] = {
      id,
      level: isLeaf ? 'chapter' : levelName,
      levelName: '',
      title: trim(trimmedTitle),
      number: number.replace(/[A-Z]{2,}/, ''),
      content: isLeaf ? paragraphsToXml(paragraphs) : null,
      hasParts: false,
      hasSchedules: false,
      hasCrossheadings: false,
      isLeaf,
      leaves,
      descendants: descendants.map(n => n.id),
      path: path.map((id, i) => toId(path.slice(0, i + 1))),
      getNode,
      originalNode: node
    }

    return { leaves, id }
  }

  const toContentNode = function ({ paragraphs }, path, node) {
    const id = toId(path)
    nodes[id] = {
      id,
      level: 'chapter',
      levelName: '',
      title: '',
      number: '',
      content: paragraphsToXml(paragraphs),
      hasParts: false,
      hasSchedules: false,
      hasCrossheadings: false,
      isLeaf: true,
      leaves: [],
      descendants: [],
      path: path.map((id, i) => toId(path.slice(0, i + 1))),
      getNode,
      originalNode: node
    }

    return { id, leaves: [] }
  }

  const root = toId([rootId])

  toNode(sections[rootId])

  return {
    versionId,
    root,
    nodes
  }
}

export const toTakeoverCodeVersions = function (data) {
  return parseJson(data).map(({ id, ...etc }) => ({ versionId: id, ...etc }))
}
